import type { TourAnchor } from '~/helpers/tour/anchors'
import { useQuery } from '@apollo/client/react'
import { GraduationCap } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import Spinner from '~/components/Spinner'
import { GQL_MY_SESSIONS } from '~/gql/sessions/sessions'
import { dayPeriod, focusLine, learnerFocus } from '~/helpers/learner/greeting'
import { completionStatus, isPath, sessionHref } from '~/helpers/learner/session-view'
import { useAuth } from '~/helpers/store/auth'
import { tourAnchor } from '~/helpers/tour/anchors'
import LearnerFrame from '~/pages/scorm/components/LearnerFrame'
import LearnerHero from '~/pages/scorm/components/LearnerHero'
import { CourseCard, PathCard } from '~/pages/scorm/components/SessionCard'

/**
 * Ordre de lecture à l'intérieur d'une section : ce qui est commencé d'abord,
 * ce qui est terminé en dernier.
 *
 * L'apprenant vient pour savoir ce qu'il lui reste à faire. Trier par date
 * d'inscription mettait une formation bouclée il y a trois mois avant celle
 * qu'il a laissée à moitié hier.
 */
const ORDER = { INCOMPLETE: 0, NOT_ATTEMPTED: 1, COMPLETED: 2 } as const

/**
 * Accueil apprenant (#74).
 *
 * Deux sections, parcours puis formations, et non un seul tas de cartes : un
 * parcours et un cours ne se suivent pas de la même façon — l'un se traverse en
 * plusieurs fois avec des étapes qui s'ouvrent, l'autre se lance et se termine.
 * Les mélanger obligeait la carte à porter les deux formes à la fois, d'où sa
 * densité inégale.
 */
function MySessions() {
  const { t } = useTranslation()
  const { payload } = useAuth()
  const { data, loading } = useQuery(GQL_MY_SESSIONS, { fetchPolicy: 'cache-and-network' })

  const sessions = data?.mySessions ?? []
  // Un parcours ne compte comme terminé que si TOUS ses modules le sont : le
  // bandeau du haut dit la même chose que chaque carte, pas autre chose.
  const completedCount = sessions.filter(s => s.progress?.completed === true).length

  // Un prénom vide est atteignable : le provisioning SSO retombe sur `''` quand
  // ni le claim ni le display name ne donnent rien (`oidc.service.ts`,
  // `saml.service.ts`). Sans ce `trim`, l'accueil dirait « Bonjour  ! ».
  const firstName = payload?.firstName?.trim() ?? ''
  const greeting = t(`home.greeting.${dayPeriod()}`, {
    context: firstName ? 'named' : undefined,
    name: firstName,
  })
  const situation = learnerFocus(sessions)
  const focus = focusLine(situation, t)
  // Le bouton du bandeau mène à la formation que nomme la phrase, quand elle en
  // nomme une et qu'elle s'ouvre : un cours fermé n'a nulle part où aller.
  const target = situation && 'sessionId' in situation
    ? sessions.find(s => s.id === situation.sessionId)
    : undefined
  const href = target && sessionHref(target)
  const action = href
    ? { href, label: t(situation?.kind === 'start' ? 'home.hero.start' : 'home.hero.resume') }
    : null

  const byProgress = (a: typeof sessions[number], b: typeof sessions[number]) =>
    ORDER[completionStatus(a)] - ORDER[completionStatus(b)]
  const paths = sessions.filter(isPath).sort(byProgress)
  const courses = sessions.filter(s => !isPath(s)).sort(byProgress)

  return (
    <LearnerFrame label={t('home.title')}>
      <LearnerHero
        greeting={greeting}
        focus={focus}
        action={action}
        completed={completedCount}
        total={sessions.length}
      />

      {loading && sessions.length === 0
        ? (
            <div className="flex justify-center p-16">
              <Spinner className="size-6 text-muted-foreground" />
            </div>
          )
        : sessions.length === 0
          ? (
              <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-border p-12 text-center">
                <GraduationCap className="size-10 text-muted-foreground" />
                <p className="font-medium">{t('sessions.my.empty')}</p>
                <p className="max-w-sm text-sm text-muted-foreground">{t('home.empty.hint')}</p>
              </div>
            )
          : (
              <div className="flex flex-col gap-10">
                {paths.length > 0 && (
                  <Section title={t('home.section.paths')} count={paths.length} anchor="learner.paths">
                    {paths.map(s => <PathCard key={s.id} session={s} />)}
                  </Section>
                )}
                {courses.length > 0 && (
                  <Section title={t('home.section.courses')} count={courses.length} anchor="learner.courses">
                    {courses.map(s => <CourseCard key={s.id} session={s} />)}
                  </Section>
                )}
              </div>
            )}
    </LearnerFrame>
  )
}

/**
 * Les cartes d'une rangée prennent la hauteur de la plus haute (#307) : leurs
 * actions calées en bas, le vide tombe au-dessus des boutons et la rangée reste
 * droite.
 *
 * `auto-fit` sur une colonne de 20 rem au moins : une colonne sur téléphone,
 * jusqu'à trois sur grand écran, et une carte seule prend toute la rangée au
 * lieu de laisser deux tiers de vide (elle se couche alors, cf. `SessionCardFrame`).
 */
function Section({ title, count, anchor, children }: {
  title: string
  count: number
  /** Ancre de visite guidée (#78), quand la section en fait l'objet d'une étape. */
  anchor?: TourAnchor
  children: React.ReactNode
}) {
  return (
    <section {...(anchor && tourAnchor(anchor))}>
      <h2 className="mb-4 flex items-baseline gap-2 font-heading text-lg tracking-tight">
        {title}
        <span className="text-sm font-normal tabular-nums text-muted-foreground">{`(${count})`}</span>
      </h2>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,20rem),1fr))] gap-5">
        {children}
      </div>
    </section>
  )
}

export default MySessions
